import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { NavigationBar } from '../components/NavigationBar';
import { useTheme } from '../context/ThemeContext';
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  HomeIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface QuizResult {
  score: number;
  total: number;
  answers: number[];
  flashcards: any[];
  studyTime: number;
}

export const QuizResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { score, total, answers, flashcards, studyTime } = location.state as QuizResult;
  const percentage = Math.round((score / total) * 100);

  const getGrade = (percentage: number) => {
    if (percentage >= 90) return { text: 'Excellent!', color: 'text-green-500' };
    if (percentage >= 80) return { text: 'Great Job!', color: 'text-green-600' };
    if (percentage >= 70) return { text: 'Good Work!', color: 'text-yellow-500' };
    if (percentage >= 60) return { text: 'Keep Practicing!', color: 'text-yellow-600' };
    return { text: 'Need More Practice', color: 'text-red-500' };
  };

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return hours > 0 
      ? `${hours}h ${remainingMinutes}m`
      : `${remainingMinutes}m`;
  };

  const grade = getGrade(percentage);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      <NavigationBar />
      <div className="pt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/60 dark:bg-gray-800/60 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-gray-100/50 dark:border-gray-700/50"
          >
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Quiz Results</h1>
              <p className={`mt-2 text-2xl font-semibold ${grade.color} dark:text-opacity-90`}>
                {grade.text}
              </p>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="text-5xl font-bold text-indigo-600 dark:text-indigo-400">
                    {percentage}%
                  </div>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">
                    Score: {score} / {total}
                  </p>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-white">
                    {total}
                  </div>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">
                    Questions
                  </p>
                </div>
                <div className="flex flex-col items-center justify-center">
                  <ClockIcon className="h-8 w-8 text-green-500 mb-2" />
                  <div className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatTime(studyTime)}
                  </div>
                  <p className="mt-1 text-gray-600 dark:text-gray-400">
                    Study Time
                  </p>
                </div>
              </div>
            </div>

            <div className="relative mt-8">
              <div className="w-full h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${percentage}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 dark:from-indigo-400 dark:to-purple-500"
                ></motion.div>
              </div>
            </div>

            <div className="mt-12">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                Question Review
              </h2>
              <div className="space-y-6">
                {flashcards.map((flashcard, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white/80 dark:bg-gray-800/80 rounded-lg p-6 shadow-sm border border-gray-100/50 dark:border-gray-700/50"
                  >
                    <div className="flex items-start space-x-4">
                      {answers[index] === flashcard.correctAnswer ? (
                        <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0" />
                      ) : (
                        <XCircleIcon className="h-6 w-6 text-red-500 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                          {flashcard.question}
                        </h3>
                        <div className="mt-2 space-y-2">
                          {flashcard.options.map((option: string, optionIndex: number) => (
                            <div
                              key={optionIndex}
                              className={`p-3 rounded-lg ${
                                optionIndex === flashcard.correctAnswer
                                  ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700'
                                  : optionIndex === answers[index]
                                  ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700'
                                  : 'bg-gray-50 dark:bg-gray-700/50 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600'
                              }`}
                            >
                              {option}
                              {optionIndex === flashcard.correctAnswer && (
                                <span className="ml-2 text-green-600 dark:text-green-400 text-sm">
                                  (Correct Answer)
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="mt-8 flex justify-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/quizzes')}
                className="inline-flex items-center px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-md text-base font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm"
              >
                <HomeIcon className="h-5 w-5 mr-2" />
                Back to Collections
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(-2)}
                className="inline-flex items-center px-6 py-3 border border-transparent rounded-md text-base font-medium text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 dark:from-indigo-600 dark:to-purple-700 dark:hover:from-indigo-700 dark:hover:to-purple-800 shadow-sm"
              >
                <ArrowPathIcon className="h-5 w-5 mr-2" />
                Try Again
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};